import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { trackEvent } from '@/utils/analyticsTrack';

export type SubscriptionPlan = 'free' | 'basic' | 'premium' | 'vip';

export interface AppUser extends User {
  firstName?: string;
  role?: string;
  plan?: string;
  isSubscribed?: boolean;
  billingPeriod?: string;
  subscriptionEndDate?: string;
  emailConfirmed?: boolean;
  whatsapp?: string;
}

export interface TrialInfo {
  isActive: boolean;
  daysRemaining: number;
  isExpired: boolean;
  status: string;
  statusMessage: string;
}

interface AuthContextType {
  user: AppUser | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  profileLoaded: boolean;
  login: (email: string, password: string) => Promise<{ error: any }>;
  register: (firstName: string, email: string, phone: string, password: string, whatsapp: string) => Promise<{ error: any; outcome?: 'new_user' | 'resent_confirmation' | 'already_confirmed' }>;
  logout: () => Promise<void>;
  updateUserPlan: (plan: string) => Promise<void>;
  getTrialInfo: () => TrialInfo;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const { toast } = useToast();

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      console.log('Perfil do usuário carregado:', profile);
      return profile;
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      return null;
    }
  };

  const syncEmailConfirmationStatus = async (userId: string, authEmailConfirmed: boolean) => {
    try {
      console.log(`Sincronizando email_confirmed para usuário ${userId}: ${authEmailConfirmed}`);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          email_confirmed: authEmailConfirmed,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);
      
      if (error) {
        console.error('Error updating email_confirmed:', error);
      } else {
        console.log(`Email confirmation status updated for user ${userId}: ${authEmailConfirmed}`);
      }
    } catch (error) {
      console.error('Error in syncEmailConfirmationStatus:', error);
    }
  };

  // Cria AppUser MÍNIMO sem tocar no banco — usado como fallback imediato
  // para destravar isAuthenticated logo após o login.
  // IMPORTANTE: status sem assinatura (plan: null, isSubscribed: false) para
  // NÃO liberar conteúdo premium acidentalmente. ProtectedRoute redireciona
  // para /planos enquanto o profile não chega.
  const createMinimalAppUser = (session: Session): AppUser => {
    const authEmailConfirmed = session.user.email_confirmed_at !== null;
    return {
      ...session.user,
      firstName: '',
      role: 'user',
      plan: null as any,
      isSubscribed: false,
      billingPeriod: undefined,
      subscriptionEndDate: undefined,
      emailConfirmed: authEmailConfirmed,
      whatsapp: ''
    };
  };

  const createAppUser = async (session: Session): Promise<AppUser> => {
    const profile = await fetchUserProfile(session.user.id);
    const authEmailConfirmed = session.user.email_confirmed_at !== null;

    // Profile fetch falhou → mínimo sem assinatura (não libera premium).
    if (!profile) {
      console.warn('Profile fetch falhou — usando AppUser mínimo (sem assinatura).');
      return createMinimalAppUser(session);
    }

    const profileEmailConfirmed = (profile as any)?.email_confirmed || false;
    if (profileEmailConfirmed !== authEmailConfirmed) {
      // fire-and-forget — não bloqueia
      syncEmailConfirmationStatus(session.user.id, authEmailConfirmed).catch(() => {});
      (profile as any).email_confirmed = authEmailConfirmed;
    }

    const appUser: AppUser = {
      ...session.user,
      firstName: (profile as any)?.first_name || '',
      role: (profile as any)?.role || 'user',
      plan: (profile as any)?.plan || 'free',
      isSubscribed: (profile as any)?.is_subscribed || false,
      billingPeriod: (profile as any)?.billing_period,
      subscriptionEndDate: (profile as any)?.subscription_end_date,
      emailConfirmed: authEmailConfirmed,
      whatsapp: (profile as any)?.whatsapp || ''
    };
    return appUser;
  };

  // Mescla enriquecimento sem REBAIXAR um assinante já confirmado.
  // Regra: se prev.isSubscribed === true, mantém true mesmo se enriquecimento
  // vier com false (proteção contra fetch intermitente).
  const mergePreservingSubscription = (prev: AppUser | null, next: AppUser): AppUser => {
    if (!prev) return next;
    if (prev.id !== next.id) return next;
    const wasSubscribed = prev.isSubscribed === true && prev.plan && prev.plan !== 'free';
    if (wasSubscribed && (!next.isSubscribed || next.plan === 'free' || !next.plan)) {
      console.warn('Preservando status de assinante — enriquecimento traria rebaixamento.');
      return {
        ...next,
        plan: prev.plan,
        isSubscribed: prev.isSubscribed,
        billingPeriod: prev.billingPeriod || next.billingPeriod,
        subscriptionEndDate: prev.subscriptionEndDate || next.subscriptionEndDate,
      };
    }
    return next;
  };

  useEffect(() => {
    let isInitialLoad = true;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    const finishLoading = () => {
      if (isInitialLoad) {
        console.log('Definindo isLoading = false');
        setIsLoading(false);
        isInitialLoad = false;
      }
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
    };

    // Timeout de segurança: nunca deixar isLoading nem profileLoaded travados
    safetyTimer = setTimeout(() => {
      if (isInitialLoad) {
        console.warn('Auth init timeout (3s) — forçando isLoading=false');
        finishLoading();
      }
      setProfileLoaded((prev) => {
        if (!prev) console.warn('profileLoaded forçado=true após timeout de 3s');
        return true;
      });
    }, 3000);

    const enrichUserAsync = (sess: Session) => {
      // fire-and-forget — não bloqueia o auth state
      createAppUser(sess)
        .then((enriched) => {
          setUser((prev) => mergePreservingSubscription(prev, enriched));
          setProfileLoaded(true);
        })
        .catch((err) => {
          console.error('Erro ao enriquecer AppUser (mantendo mínimo):', err);
          // Mesmo em falha, liberamos o gate — usuário cai em /planos pelo fluxo normal.
          setProfileLoaded(true);
        });
    };

    const initializeAuth = async () => {
      console.log('Inicializando autenticação...');

      // Listener de auth — set síncrono primeiro, enriquecimento depois
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          console.log('Auth state changed:', event, session?.user?.email);
          setSession(session);

          if (session?.user) {
            // 1) Mínimo síncrono → isAuthenticated vira true imediatamente
            setUser((prev) => {
              if (prev && prev.id === session.user.id) return prev; // já temos user
              // entrando em estado mínimo → barrar o gate até enriquecer
              setProfileLoaded(false);
              return createMinimalAppUser(session);
            });
            // 2) Enriquecimento assíncrono sem rebaixar
            enrichUserAsync(session);
          } else {
            // Só zerar em SIGNED_OUT/USER_DELETED — evita "blip" de eventos
            // transitórios sem sessão que causavam redirect espúrio para /planos.
            if (event === 'SIGNED_OUT' || (event as string) === 'USER_DELETED') {
              setUser(null);
              setProfileLoaded(false);
            }
          }

          finishLoading();
        }
      );

      // Sessão existente — try/catch para tokens órfãos
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          const msg = (error.message || '').toLowerCase();
          if (msg.includes('refresh') || msg.includes('token')) {
            console.warn('Token inválido detectado — limpando localStorage');
            try {
              Object.keys(localStorage)
                .filter((k) => k.startsWith('sb-') || k.includes('supabase.auth'))
                .forEach((k) => localStorage.removeItem(k));
            } catch {}
          }
        }

        console.log('Initial session check:', session?.user?.email);
        setSession(session);

        if (session?.user) {
          setUser((prev) => prev ?? createMinimalAppUser(session));
          enrichUserAsync(session);
        }
      } catch (err) {
        console.error('Erro em getSession (init):', err);
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith('sb-') || k.includes('supabase.auth'))
            .forEach((k) => localStorage.removeItem(k));
        } catch {}
      }

      finishLoading();

      return () => subscription.unsubscribe();
    };

    let cleanup: (() => void) | undefined;
    initializeAuth().then((cleanupFn) => {
      cleanup = cleanupFn;
    });

    return () => {
      if (safetyTimer) clearTimeout(safetyTimer);
      if (cleanup) cleanup();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        console.error('Login error:', error);
        return { error };
      }

      // Track login event (fire-and-forget)
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        trackEvent("login", { method: "password" }, s?.user?.id || null);
      } catch { /* noop */ }

      // Track user session (fire-and-forget)
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.access_token) {
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-user-session`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${currentSession.access_token}`,
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
            },
            body: JSON.stringify({ user_agent: navigator.userAgent })
          }).catch(err => console.error('Track session error:', err));
        }
      } catch (trackErr) {
        console.error('Track session error:', trackErr);
      }

      return { error: null };
    } catch (error) {
      console.error('Login error:', error);
      return { error };
    }
  };

  const register = async (firstName: string, email: string, phone: string, password: string, whatsapp: string) => {
    try {
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedPhone = phone.trim();
      const trimmedWhatsapp = whatsapp.trim();
      
      // Validar WhatsApp obrigatório (mantendo a obrigatoriedade)
      if (!trimmedWhatsapp) {
        return { error: { message: 'WhatsApp é obrigatório para o cadastro' } };
      }
      
      console.log('Registrando usuário com WhatsApp:', trimmedWhatsapp);
      
      // CORRIGIR: Usar callback correto para confirmação de email
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { data, error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            firstName: firstName.trim(),
            phone: trimmedPhone,
            whatsapp: trimmedWhatsapp
          }
        }
      });

      if (error) {
        console.error('Registration error:', error);
        
        // Detectar erro específico de usuário já cadastrado
        if (error.message.includes('User already registered') || error.message.includes('user_repeated_signup')) {
          console.log('Usuário já existe e já confirmou email - direcionando para login');
          return { error: { message: 'User already registered' }, outcome: 'already_confirmed' as const };
        }
        
        return { error };
      }

      // Analisar o resultado do signUp para determinar o outcome
      let outcome: 'new_user' | 'resent_confirmation' | 'already_confirmed' = 'new_user';
      
      if (data.user && !data.user.email_confirmed_at) {
        // Usuário criado mas ainda não confirmou email
        if (data.user.created_at && new Date(data.user.created_at).getTime() < Date.now() - 60000) {
          // Se o created_at é muito antigo (mais de 1 minuto), provavelmente foi reenvio
          outcome = 'resent_confirmation' as const;
          console.log('Email de confirmação reenviado para usuário existente');
        } else {
          outcome = 'new_user' as const;
          console.log('Novo usuário criado - primeiro registro');
        }
      } else if (data.user && data.user.email_confirmed_at) {
        // Usuário já confirmou email - não deveria acontecer, mas tratar
        outcome = 'already_confirmed' as const;
        console.log('Usuário já confirmou email anteriormente');
      }

      // Registrar lead de afiliado apenas para novos usuários
      if (outcome === 'new_user' && data.user) {
        try {
          const savedCode = localStorage.getItem('affiliateCode');
          if (savedCode) {
            console.log('Registrando lead de afiliado para novo usuário:', savedCode, data.user.id);
            const { data: leadData, error: leadError } = await supabase.rpc('record_affiliate_lead', {
              p_affiliate_code: savedCode,
              p_customer_user_id: data.user.id
            });
            if (leadError) {
              console.error('Erro ao registrar lead no signUp:', leadError);
            } else {
              console.log('Resultado record_affiliate_lead no signUp:', leadData);
            }
          }
        } catch (leadErr) {
          console.error('Exceção ao registrar lead no signUp:', leadErr);
        }
      }

      console.log(`Registro processado com sucesso - Outcome: ${outcome}, WhatsApp: ${trimmedWhatsapp}`);
      try {
        if (outcome === 'new_user') {
          trackEvent("signup_completed", { outcome }, data.user?.id || null);
        }
      } catch { /* noop */ }
      return { error: null, outcome };
    } catch (error) {
      console.error('Registration error:', error);
      return { error };
    }
  };

  const logout = async () => {
    // Sempre limpamos o estado local e mostramos sucesso, mesmo se a sessão Supabase já estiver ausente.
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        const msg = (error.message || '').toLowerCase();
        const isSessionMissing =
          msg.includes('auth session missing') ||
          msg.includes('session_not_found') ||
          (error as any)?.name === 'AuthSessionMissingError';
        if (!isSessionMissing) {
          console.error('Logout error (não crítico):', error);
        }
      }
    } catch (error: any) {
      const msg = (error?.message || '').toLowerCase();
      if (!msg.includes('auth session missing')) {
        console.error('Logout error (catch):', error);
      }
    } finally {
      // Limpeza local sempre ocorre
      setUser(null);
      setSession(null);
      setProfileLoaded(false);
      try {
        // Limpar resquícios de tokens armazenados localmente
        Object.keys(localStorage)
          .filter((k) => k.startsWith('sb-') || k.includes('supabase.auth'))
          .forEach((k) => localStorage.removeItem(k));
      } catch {}
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
    }
  };

  const updateUserPlan = async (plan: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          plan,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating user plan:', error);
        toast({
          title: "Erro",
          description: "Não foi possível atualizar o plano.",
          variant: "destructive",
        });
        return;
      }

      // Update local user state
      setUser(prev => prev ? { ...prev, plan } : null);
      
      toast({
        title: "Sucesso",
        description: `Plano atualizado para ${plan}.`,
      });
    } catch (error) {
      console.error('Error updating user plan:', error);
    }
  };

  const getTrialInfo = (): TrialInfo => {
    if (!user) {
      return { 
        isActive: false, 
        daysRemaining: 0, 
        isExpired: true,
        status: 'expired',
        statusMessage: 'Usuário não autenticado'
      };
    }
    
    // NOVA LÓGICA: Apenas usuários com planos PAGOS e ATIVOS têm acesso
    // Usuários FREE não têm mais acesso de teste gratuito
    if (user.plan === 'free' || !user.isSubscribed) {
      console.log('FREE USER or NOT SUBSCRIBED - blocking access for user:', user.id);
      return { 
        isActive: false, 
        daysRemaining: 0, 
        isExpired: true,
        status: 'free_blocked',
        statusMessage: 'Acesso disponível apenas para assinantes'
      };
    }
    
    // Para usuários com planos pagos
    if (user.isSubscribed && user.subscriptionEndDate) {
      const endDate = new Date(user.subscriptionEndDate);
      const now = new Date();
      const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining > 7) {
        return { 
          isActive: true, 
          daysRemaining, 
          isExpired: false,
          status: 'subscription_active',
          statusMessage: `Plano ativo por mais ${daysRemaining} dias`
        };
      } else if (daysRemaining > 0) {
        return { 
          isActive: true, 
          daysRemaining, 
          isExpired: false,
          status: 'subscription_expiring',
          statusMessage: `Plano expirando em ${daysRemaining} dia${daysRemaining === 1 ? '' : 's'}`
        };
      } else {
        return { 
          isActive: false, 
          daysRemaining: 0, 
          isExpired: true,
          status: 'subscription_expired',
          statusMessage: 'Assinatura expirada'
        };
      }
    }
    
    // CRÍTICO: Se chegou até aqui, significa que o usuário não se enquadra nas condições acima
    // Isso indica assinatura expirada ou dados inconsistentes - bloquear acesso
    console.warn('User access blocked - subscription status unclear:', {
      plan: user.plan,
      isSubscribed: user.isSubscribed,
      subscriptionEndDate: user.subscriptionEndDate,
      userId: user.id
    });
    
    return { 
      isActive: false, 
      daysRemaining: 0, 
      isExpired: true,
      status: 'subscription_expired',
      statusMessage: 'Assinatura expirada'
    };
  };

  // Função para verificar assinatura (placeholder para integração futura)
  const checkSubscription = async () => {
    console.log('Checking subscription status...');
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user && !!session,
    profileLoaded,
    login,
    register,
    logout,
    updateUserPlan,
    getTrialInfo,
    checkSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
